/**
 * Accessibility Helpers
 * Focus trapping for modals/drawers and focus return on close.
 * Loaded globally via theme.liquid.
 */

const A11y = {
  /**
   * Get all focusable elements inside a container.
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  getFocusableElements(container) {
    const selectors = [
      'a[href]:not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])',
      'details:not([tabindex="-1"])',
      'summary:not([tabindex="-1"])'
    ];
    return Array.from(container.querySelectorAll(selectors.join(',')))
      .filter(el => !el.hasAttribute('hidden') && el.offsetParent !== null);
  },

  /**
   * Trap focus within a container element.
   * Returns a cleanup function to remove the trap.
   * @param {HTMLElement} container
   * @returns {Function} cleanup function
   */
  trapFocus(container) {
    const handler = (event) => {
      if (event.key !== 'Tab') return;

      const focusable = A11y.getFocusableElements(container);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  },

  /**
   * Move focus into a container (first focusable element or container itself).
   * @param {HTMLElement} container
   */
  moveFocusInto(container) {
    const opts = { preventScroll: true, focusVisible: false };
    const focusable = A11y.getFocusableElements(container);
    if (focusable.length > 0) {
      focusable[0].focus(opts);
    } else if (container.hasAttribute('tabindex')) {
      container.focus(opts);
    }
  }
};

/**
 * Mobile Menu Focus Trap
 * Traps focus when mobile menu is open, returns focus to toggle on close.
 */
(function initMobileMenuA11y() {
  const toggle = document.querySelector('.mobile-toggle-wrapper');
  const menu = document.querySelector('#mobile-menu');
  if (!toggle || !menu) return;

  let removeTrap = null;
  const summary = toggle.querySelector('.mobile-toggle');

  const observer = new MutationObserver(() => {
    const isOpen = toggle.hasAttribute('open') || toggle.classList.contains('active');

    if (isOpen) {
      // Delay to let the CSS transition start
      setTimeout(() => {
        A11y.moveFocusInto(menu);
        removeTrap = A11y.trapFocus(menu);
      }, 100);
    } else {
      if (removeTrap) {
        removeTrap();
        removeTrap = null;
      }
      // Return focus to the toggle
      if (summary) summary.focus();
    }
  });

  observer.observe(toggle, { attributes: true, attributeFilter: ['open', 'class'] });

  // Also handle Escape returning focus
  menu.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      toggle.removeAttribute('open');
      toggle.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      if (summary) summary.focus();
    }
  });
})();

/**
 * Cart Drawer Focus Trap
 * Traps focus when cart drawer is open, returns focus to trigger on close.
 */
(function initCartDrawerA11y() {
  const cartDrawer = document.getElementById('Cart-Drawer');
  if (!cartDrawer) return;

  let removeTrap = null;
  let lastFocusedElement = null;

  const observer = new MutationObserver(() => {
    const isOpen = cartDrawer.classList.contains('active');

    if (isOpen) {
      lastFocusedElement = document.activeElement;
      setTimeout(() => {
        A11y.moveFocusInto(cartDrawer);
        removeTrap = A11y.trapFocus(cartDrawer);
      }, 100);
    } else {
      if (removeTrap) {
        removeTrap();
        removeTrap = null;
      }
      if (lastFocusedElement) {
        lastFocusedElement.focus();
        lastFocusedElement = null;
      }
    }
  });

  observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });

  // Handle Escape key inside cart drawer
  cartDrawer.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      cartDrawer.classList.remove('active');
      unlockScroll();
      if (lastFocusedElement) {
        lastFocusedElement.focus();
        lastFocusedElement = null;
      }
    }
  });

  // Make side-panel-close respond to Enter/Space
  const closeBtn = cartDrawer.querySelector('side-panel-close');
  if (closeBtn) {
    closeBtn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        closeBtn.click();
      }
    });
  }
})();

/**
 * Cart update announcements for screen readers (Issue 8)
 */
(function initCartAnnouncements() {
  // Create a live region for cart announcements
  const liveRegion = document.createElement('div');
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');
  liveRegion.classList.add('visually-hidden');
  liveRegion.id = 'cart-live-region';
  document.body.appendChild(liveRegion);

  // Listen for cart update events
  document.addEventListener('cart:updated', (e) => {
    const detail = e.detail || {};
    if (detail.message) {
      liveRegion.textContent = detail.message;
    } else if (detail.item_count !== undefined) {
      liveRegion.textContent = `Cart updated. ${detail.item_count} item${detail.item_count !== 1 ? 's' : ''} in cart.`;
    }
  });

  // Also listen for custom theme events
  document.addEventListener('cart-drawer:open', () => {
    const cartItems = document.querySelectorAll('.product-cart-item');
    liveRegion.textContent = `Cart drawer opened. ${cartItems.length} item${cartItems.length !== 1 ? 's' : ''} in cart.`;
  });
})();

/**
 * Filter results announcement (Issue 7.2)
 */
(function initFilterAnnouncements() {
  const liveRegion = document.createElement('div');
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');
  liveRegion.classList.add('visually-hidden');
  liveRegion.id = 'filter-live-region';
  document.body.appendChild(liveRegion);

  // Watch for collection grid updates after filtering
  const collectionGrid = document.querySelector('.thb-product-grid, .collection-products');
  if (collectionGrid) {
    const observer = new MutationObserver(() => {
      const count = collectionGrid.querySelectorAll('product-card').length;
      liveRegion.textContent = `Showing ${count} product${count !== 1 ? 's' : ''}`;
    });
    observer.observe(collectionGrid, { childList: true, subtree: true });
  }
})();
